import { ArrowLeftIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useOverflowFade } from './OverflowFadeText';

import { SegmentedToggle, type SegmentedToggleOption } from '@/components/ui/segmented-toggle';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { PAGE_GUTTER, TEXT_FADE_RIGHT } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface TabOption {
  id: string;
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
  tabs?: TabOption[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  noPadding?: boolean;
  variant?: 'page' | 'section';
  tabVariant?: 'underline' | 'pills';
  bottomComponent?: React.ReactNode;
  centerComponent?: React.ReactNode;
  /** Nested view: render a ← back arrow (returns to this href) INSTEAD of the
   *  hamburger — the two are mutually exclusive. */
  backHref?: string | null;
  backLabel?: string | null;
}

const PageHeaderComponent: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  className,
  tabs,
  activeTab,
  onTabChange,
  noPadding = false,
  variant = 'page',
  tabVariant = 'underline',
  bottomComponent,
  centerComponent,
  backHref = null,
  backLabel = null,
}) => {
  const handleTabClick = React.useCallback(
    (tabId: string) => {
      if (onTabChange) {
        onTabChange(tabId);
      } else {
        const tab = tabs?.find((t) => t.id === tabId);
        if (tab?.href) {
          window.location.href = tab.href;
        }
      }
    },
    [onTabChange, tabs],
  );

  const toggleOptions: SegmentedToggleOption[] = React.useMemo(() => {
    return (tabs || []).map((tab) => ({
      value: tab.id,
      label: tab.label,
      icon: tab.icon,
    }));
  }, [tabs]);

  const { ref: titleRef, isOverflowing: titleIsOverflowing } =
    useOverflowFade<HTMLHeadingElement>();

  return (
    <>
      <header className={cn('w-full', className)}>
        <div
          className={cn(
            // Grid (mobile only, md:flex reverts to the old behavior). The
            // side columns are both `1fr` — NOT `auto` — so they always end
            // up the SAME width as each other regardless of their actual
            // content (a lone 40px nav icon vs a 2-icon ~74px actions pill),
            // which is what makes the middle title column's midpoint land on
            // true page-center on every route, not just ones where the two
            // sides happen to need equal space. This replaced an earlier
            // attempt that reserved a fixed 80px on BOTH sides unconditionally
            // — that fixed the 2-icon routes but wasted 40px of title space on
            // every OTHER route (single nav icon never needs more than 40px),
            // clipping long titles like "Mango Splash 1-Week Launch" on
            // /projects/:id. `1fr` costs nothing extra: each side's content
            // still sits flush against its own row edge (icons never move),
            // and the two tracks are only ever as wide as leftover space
            // divides them, equally, whatever that happens to be.
            'relative grid grid-cols-[1fr_auto_1fr] md:flex items-center gap-2 min-h-[56px] md:min-h-[52px]',
            // px-4 mobile matches the design system's documented page-padding
            // standard (DESIGN-PRINCIPLES §Breathing: "p-4 mobile, p-6
            // desktop") — was px-3 (12px), a 4px mismatch against page body
            // content (composer, cards) that consistently uses px-4/p-4,
            // making the header icons read as slightly inset from everything
            // below them.
            // ⚠️ THE HEADER IS NOT INSIDE THE PAGE COLUMN, AND THAT IS AN
            // OPEN DECISION, NOT AN OVERSIGHT. `PageFrame` owns the content's
            // measure; this row is full-bleed, so on a framed route the title
            // does not sit above the content.
            //
            // Measured 2026-08-27 at 1440x900 on `/briefs/:id`: content column
            // 104→1384, header row 48→1440 — the breadcrumb starts 56px LEFT
            // of the first card and the `...` ends 56px RIGHT of it (68px at
            // 1512). Founder the same day: _"every page should not control its
            // own max width. That should be sized in whatever parent container
            // it is in."_
            //
            // Capping this row at `PAGE_MEASURE` was tried and REVERTED the
            // same hour: it fixes the ~26 routes that render a `PageFrame` and
            // BREAKS every full-bleed workspace route, which is a real and
            // deliberate second shape. Measured on the Concept page with the
            // cap in place: shell 48→1440, header row 104→1384 — the title
            // landed 80px inside the editor pane and the `...` 80px inside the
            // rendition pane, where both had been flush. A single global
            // measure here is wrong.
            //
            // The carrier is the unsolved part, and it must not be an optional
            // prop: `surface="workspace"` already proved that shape fails, with
            // 1 of 5 workspace surfaces remembering to pass it. The two live
            // candidates are (a) `PageFrame` registering itself so the shell
            // takes the measure from the frame actually on the page — correct,
            // but it can flash full-bleed for one paint on a route change — and
            // (b) making the frame the header's parent, which is an AppLayout
            // change. Pick one deliberately; do not re-add the cap alone.
            noPadding
              ? ''
              : tabs && tabs.length > 0
                ? cn(PAGE_GUTTER, 'pt-3 sm:pt-2 pb-1')
                : cn(PAGE_GUTTER, 'py-3 sm:py-2'),
          )}
        >
          {/* LEFT — one nav element, MOBILE ONLY: ← back (nested) or ☰ menu
              (root). On desktop the persistent sidebar covers ☰, and the
              breadcrumb's root crumb (e.g. "Projects /") covers back — so the
              whole slot is md:hidden and the title flows to the left edge.
              Both states share the same glassy `.header-nav-btn` circle AND
              the same `size-nav-icon-mobile` (40px) footprint — the back
              arrow was previously hardcoded to `size-9` (36px), a 4px
              mismatch against the hamburger that made the nav circle visibly
              resize as a user moved between root and nested pages. The
              hamburger previously fell back to SidebarTrigger's own bare
              `bg-transparent` styling too, so root pages (Home, Projects,
              Library, Calendar list, ...) showed an ungrouped icon while
              nested pages (back-arrow) showed the correct glassy circle.
              `header-nav-btn`'s CSS background wins over SidebarTrigger's own
              `bg-transparent` default (verified via computed style — the
              custom class is declared later in the cascade than the
              Tailwind utility). */}
          <div className="z-10 flex shrink-0 items-center md:hidden">
            {backHref ? (
              <Link
                to={backHref}
                aria-label={backLabel ?? 'Back'}
                className="header-nav-btn inline-flex size-nav-icon-mobile max-md:size-11 md:size-nav-icon-desktop shrink-0 items-center justify-center rounded-full text-foreground/80 transition-colors"
              >
                <ArrowLeftIcon weight="bold" className="size-5 md:size-4" />
              </Link>
            ) : (
              <SidebarTrigger className="header-nav-btn shrink-0 rounded-full max-md:size-11" />
            )}
          </div>

          {/* CENTER — title, centered + overflow-faded on mobile, left-aligned desktop. */}
          <div className="page-title-centered pointer-events-none min-w-0 w-full md:flex-1">
            {title ? (
              <h1
                ref={titleRef}
                className={cn(
                  // `text-center md:text-left` is the mobile centering the
                  // `.page-title-centered` wrapper was supposed to deliver and
                  // could not. That class centers its CHILD, but the child here
                  // is `w-full` — so the h1 already fills the whole bounded slot
                  // and `justify-center` has nothing left to move. A React-node
                  // title happened to survive because it brings its own
                  // centering (`RouteBreadcrumb`'s list is `justify-center
                  // md:justify-start`), which is why /library and /projects read
                  // as centered on mobile while every PLAIN-STRING title
                  // (/insights, /calendar, /products, /board) sat hard left
                  // against the hamburger. The alignment belongs on the text, not
                  // on a wrapper that can't reach it.
                  'pointer-events-auto min-w-0 w-full whitespace-nowrap text-center md:text-left',
                  variant === 'page'
                    ? cn(
                        titleIsOverflowing && TEXT_FADE_RIGHT,
                        'text-base font-semibold tracking-tight text-foreground/90 md:text-sm md:font-medium md:text-foreground/80',
                      )
                    : cn(
                        titleIsOverflowing && TEXT_FADE_RIGHT,
                        'text-xs font-medium uppercase tracking-wider text-muted-foreground/80',
                      ),
                )}
              >
                {title}
              </h1>
            ) : null}
            {subtitle && (
              <p className="pointer-events-auto truncate text-center text-caption leading-snug text-muted-foreground md:text-left">
                {subtitle}
              </p>
            )}
          </div>

          {/* centerComponent is currently unused by any page — it's `hidden
              sm:flex` (shows from 640px) while the three-slot mobile grid
              above applies below md: (768px), so a 4th grid child in that
              640-767px band would need an explicit layout. Revisit if this
              prop gets a real caller. */}
          {centerComponent && (
            <div className="hidden sm:flex flex-1 items-center justify-center min-w-0">
              {centerComponent}
            </div>
          )}

          {/* Rendered unconditionally (not gated on `actions`) so the grid's
              third `1fr` track always exists to receive its equal share of
              leftover space — an absent grid item doesn't collapse a `1fr`
              track (unlike the `auto` track this used to be), but keeping a
              real (if empty) div here avoids the icons visibly shifting if
              `actions` becomes non-null after the initial render. */}
          <div className="z-10 ml-auto flex shrink-0 items-center justify-end">{actions}</div>
        </div>

        {tabs && tabs.length > 0 && (
          <div className="border-b border-border/40">
            <div className={cn(noPadding ? '' : PAGE_GUTTER)}>
              <SegmentedToggle
                options={toggleOptions}
                value={activeTab || ''}
                onValueChange={handleTabClick}
                variant={tabVariant === 'pills' ? 'pills' : 'tabs'}
                size="sm"
                className="justify-start no-scrollbar -mb-px"
              />
            </div>
          </div>
        )}
      </header>
      {bottomComponent}
    </>
  );
};

PageHeaderComponent.displayName = 'PageHeader';

export const PageHeader = React.memo(PageHeaderComponent);

export default PageHeader;
